import {
  Alert,
  Button,
  Heading,
  Paragraph,
} from "@digdir/designsystemet-react";
import { XMarkIcon } from "@navikt/aksel-icons";
import type { ReactNode } from "react";
import { toast as rhToast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { cn } from "@/utils";

type ToasterMessageProps = {
  messageKey: string;
  titleKey?: string;
  footer?: ReactNode;
};

export const useToaster = () => {
  const { t } = useTranslation();
  const showToast = ({
    messageKey,
    titleKey,
    footer,
    type,
  }: {
    type: "success" | "danger" | "info" | "warning";
  } & ToasterMessageProps) => {
    rhToast.custom(
      (toast) => (
        <Alert
          aria-live="assertive"
          aria-atomic="true"
          data-color={type}
          className={cn(
            "max-w-2xl relative pr-14",
            toast.visible ? "animate-toast-enter" : "animate-toast-leave",
          )}
        >
          <div className="flex flex-col gap-2">
            {titleKey && (
              <Heading level={2} data-size="xs">
                {t(titleKey)}
              </Heading>
            )}
            <Paragraph>{t(messageKey)}</Paragraph>
            {footer != null && (
              <span className="text-sm text-balder-text-subtle">{footer}</span>
            )}

            <Button
              variant="tertiary"
              data-size="sm"
              icon={true}
              className="m-0 p-0 absolute top-1 right-1"
              onClick={() => rhToast.dismiss(toast.id)}
              aria-label={t("toaster.lukk")}
            >
              <XMarkIcon aria-hidden />
            </Button>
          </div>
        </Alert>
      ),
      { duration: 20000 },
    );
  };
  return {
    success: (props: ToasterMessageProps) =>
      showToast({ ...props, type: "success" }),
    error: (props: ToasterMessageProps) =>
      showToast({ ...props, type: "danger" }),
    info: (props: ToasterMessageProps) => showToast({ ...props, type: "info" }),
    warning: (props: ToasterMessageProps) =>
      showToast({ ...props, type: "warning" }),
  };
};
